import { useState } from 'react'
import Modal from '..'
import DefaultButton from '../../Button/Button'
import FormInput from '../../Input'
import ModalClose from '../ModalClose'
import { GuestInvitation } from '../../../validations/Security'
import { CreateGuest, IcreateGuestModal } from '../../../interfaces/security'
import SecurityService from '../../../api/security'
import { showToast } from '../../../utils/toast'
import { useFormik } from 'formik'
import SafeltoShortLogo from '../../../assets/icons/SafeltoShortLogo'
import Dropdown from '../../Dropdown'
import { getLocal } from '../../../utils/local'

const CreateGuestModal = ({
  open,
  setIsOpen,
  mutate,
  onGuestCreated,
}: IcreateGuestModal) => {
  const [loading, setLoading] = useState(false)
  const [selectedResidentId, setSelectedResidentId] = useState<string | null>(null)

  const [initialValues] = useState<CreateGuest>({
    firstName: '',
    lastName: '',
    phoneNumber: '',
    property: '',
    purposeOfVisit: '',
    resident: '',
    email: '',
  })
  const getEstate = getLocal('estate')
  const estateName = getEstate.name
  const { data: residentsData, isLoading: residentsLoading } =
    SecurityService.useGetResidents({
      name: estateName,
    })
  const { data: residentProperties, isLoading: propertiesLoading, error: propertiesError, } =
    SecurityService.useGetResidentProperties(selectedResidentId)

const propertyErrorMessage = propertiesError
  ? (propertiesError)?.data?.message || (propertiesError)?.message || 'No options available'
  : '';

  const residentOptions =
    residentsData?.results.map((item) => ({
      value: item.resident.id,
      label:
        item.resident.fullName === 'undefined undefined'
          ? item.resident.name
          : item.resident.fullName,
      fullItem: item,
    })) ?? []
  const propertyOptions =
    residentProperties?.results.map((property) => ({
      value: property.id,
      label:
        property.address.length > 10
          ? property.address.slice(0, 10) + '...'
          : property.address,
    })) ?? []

  const submitForm = (values: CreateGuest) => {
    if (loading) return

    setLoading(true)
    const { firstName, lastName, ...rest } = values

    const guestData = {
      ...rest,
      name: `${firstName} ${lastName}`,
    }

    SecurityService.createGuest(guestData)
      .then((response) => {
        showToast({
          description: 'Guest created successfully',
          status: 'success',
        })
        onGuestCreated(response)
      })
      .catch((error) => {
        console.error('Error creating guest', error)
        showToast({
          description: 'Error creating guest',
          status: 'error',
        })
      })
      .finally(() => {
        mutate()
        setLoading(false)
        setIsOpen(false)
      })
  }
  const formik = useFormik({
    initialValues,
    validationSchema: GuestInvitation,
    onSubmit: submitForm,
    validateOnMount: true, // ← add this
  })

  // const formik = useFormik({
  //   initialValues,
  //   validationSchema: GuestInvitation,
  //   onSubmit: (values) => {
  //     submitForm(values)
  //   },
  // })

  return (
    <Modal
      open={open}
      close={() => setIsOpen(false)}
      widthClass="w-[calc(100%-16px)] sm:w-[90vw] md:w-full md:max-w-[700px]"
      modalClassName="flex flex-col p-4 sm:p-6 md:p-10 !rounded-10 max-h-[95vh] overflow-y-auto"
    >
      <div className="flex justify-between items-center mb-4">
        <SafeltoShortLogo />
        <ModalClose onClick={() => setIsOpen(false)} />
      </div>
      <div className="mb-4 sm:mb-6">
        <h2 className="font-semibold text-sm sm:text-base md:text-lg mb-2">Add guest</h2>
        <h5 className="font-normal text-xs sm:text-sm text-gray-600">Enter the information below</h5>
      </div>
      <div>
        <form onSubmit={formik.handleSubmit}>
          <div className="mb-6 sm:mb-8">
            <h4 className="font-bold text-sm sm:text-base md:text-lg mb-3 sm:mb-4">Destination Details</h4>
            <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 md:gap-14">
              <div className="w-full">
                <p className="font-normal text-xs sm:text-sm md:text-base leading-7 text-[#212529]">Resident</p>
                <Dropdown
                  options={residentOptions}
                  onSelect={(value) => {
                    const selected = residentOptions.find((opt) => opt.value === value)
                    const communityId = selected?.fullItem?.id
                    const residentId = selected?.value

                    setSelectedResidentId(communityId ?? null)
                    formik.setFieldValue('resident', residentId)
                  }}
                  value={formik.values.resident}
                  defaultLabel="Select resident"
                  loading={residentsLoading}
                />
              </div>
              <div className="w-full">
                <p className="text-[#212529] font-normal text-xs sm:text-sm md:text-base leading-7">Property</p>
                <Dropdown
                  options={propertyOptions}
                  onSelect={(value) => formik.setFieldValue('property', value)}
                  value={formik.values.property}
                  defaultLabel="Select Property"
                  loading={propertiesLoading}
                  emptyMessage={propertyErrorMessage}
                />
              </div>
            </div>
          </div>
          <div className="mb-4 sm:mb-6">
            <h4 className="font-bold text-sm sm:text-base md:text-lg mb-3 sm:mb-4">Guest Details</h4>
            <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 md:gap-20">
              <FormInput
                placeholder="Jude"
                type="text"
                label="Guest first name"
                name="firstName"
                value={formik.values.firstName}
                onChange={formik.handleChange}
                error={formik.touched.firstName ? formik.errors.firstName : undefined}
                labelClassName="!font-normal text-xs sm:text-sm"
              />
              <FormInput
                placeholder="Bill"
                type="text"
                label="Guest last name"
                name="lastName"
                value={formik.values.lastName}
                onChange={formik.handleChange}
                error={formik.touched.lastName ? formik.errors.lastName : undefined}
                className="font-normal"
                labelClassName="!font-normal text-xs sm:text-sm"
              />
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 md:gap-20">
            <div className="flex flex-1">
              <FormInput
                placeholder="0708555-0125"
                type="text"
                label="Guest phone number"
                name="phoneNumber"
                value={formik.values.phoneNumber}
                error={formik.touched.phoneNumber ? formik.errors.phoneNumber : undefined}
                onChange={formik.handleChange}
                labelClassName="!font-normal text-xs sm:text-sm"
              />
            </div>
            <div className="flex flex-1 mb-4 sm:mb-0">
              <FormInput
                placeholder="safelto@gmail.com"
                type="text"
                label="Guest email address"
                name="email"
                value={formik.values.email}
                error={formik.touched.email ? formik.errors.email : undefined}
                onChange={formik.handleChange}
                labelClassName="!font-normal text-xs sm:text-sm"
              />
            </div>
          </div>
          <div className="flex flex-col flex-1 gap-2 mb-5 mt-4 sm:mt-6 px-2">
            <label htmlFor="Purpose" className="font-normal text-xs sm:text-sm md:text-base text-[#212529]">
              Purpose of visit
            </label>
            <textarea
              name="purposeOfVisit"
              id="Purpose"
              value={formik.values.purposeOfVisit}
              onChange={formik.handleChange}
              rows={3}
              className="border border-solid rounded-lg bg-[#FAFAFA] resize-none p-3 text-xs sm:text-sm focus:outline-none"
              placeholder=""
            ></textarea>
            {formik.touched.purposeOfVisit && formik.errors.purposeOfVisit && (
              <p className="text-red-500 text-xs sm:text-sm mt-1">{formik.errors.purposeOfVisit}</p>
            )}
          </div>
          <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-4 justify-center sm:justify-end mt-6 sm:mt-8">
            <DefaultButton
              buttonType="secondary"
              border="none"
              fontSize="14"
              onClick={() => setIsOpen(false)}
              className="w-full sm:w-auto"
            >
              Cancel
            </DefaultButton>
            <DefaultButton
              type="submit"
              fontSize="14"
              loading={loading}
              loadingText="Submitting..."
              disabled={loading || !(formik.isValid && formik.dirty)}
              className="w-full sm:w-auto"
            >
              Submit
            </DefaultButton>
          </div>
        </form>
      </div>
    </Modal>
  )
}

export default CreateGuestModal
